import { EditOutlined, SaveOutlined } from "@ant-design/icons";
import { Col, Input, Modal, Row, Spin, Table } from "antd";
import { useFormik } from "formik";
import { get, toLower, toNumber, toString } from "lodash";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Button from "../../../components/functional_components/Button/PFButton";
import { setFilters } from "../../../redux/ducks/Filters";
import { setLoading } from "../../../redux/ducks/Loading";
import { langDate, langMonth, paginationDefaults } from "../../../utils/properties";
import { buildQueryString, formatNumber, showError, showSuccess, trimData } from "../../../utils/utilities";
import Filter from "../../../components/functional_components/Filter/Filter";
import ServiceRate from "../../../services/api/ServiceRate";
import dayjs from "dayjs";

const ServiceRates = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const currentMonth = dayjs().format("YYYYMM");
  const filters = useSelector((state) => state.FiltersDuck.filters);
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const dateFormat = langMonth[toLower(currentLanguage)];
  const [state, setState] = useState({
    lastMonthClosed: null,
    tableContent: [],
    selectedService: {},
    pagination: {
      currentPage: paginationDefaults.page,
      size: paginationDefaults.size,
      totalItems: paginationDefaults.totalItems,
    },
    productCategories: [],
    loading: false,
    modal: {
      visible: false,
      type: "",
      data: {},
    },
  });

  useEffect(() => {
    getStatus();
  }, []);

  const formikServices = useFormik({
    initialValues: {
      name: get(state.selectedService, "name", ""),
      id: get(state.selectedService, "id", ""),
      price: get(state.selectedService, "price", ""),
    },
    validate: (values) => {
      values = trimData(values);
      let errors = {};
      if (!values.price) errors.unitPrice = "Required!";
      return errors;
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      setState((prevState) => ({ ...prevState, loading: true }));
      const payload = {
        month: toNumber(filters.obj.month),
        price: values.price,
        serviceId: values.id,
      };
      const saveResponse = await ServiceRate.saveEditServiceRates(payload);
      if (!saveResponse.error) {
        showSuccess();
        closeModal();
        dataList(filters.string);
      } else {
        showError(get(saveResponse, "error.messageCode", "ER000"));
      }
      setState((prevState) => ({ ...prevState, loading: false }));
    },
  });

  const getStatus = async () => {
    dispatch(setLoading(true));
    const status = await ServiceRate.getLastStatus();
    if (!status.error && status.data) {
      setState((prevState) => ({ ...prevState, lastMonthClosed: status.data.lastMonthClosed }));
      const queryString = buildQueryString({}, { month: status.data.nextMonthToOpen });
      dispatch(setFilters(queryString));
      dataList(queryString.string);
    } else {
      showError(get(status, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const dataList = async (querystring) => {
    dispatch(setLoading(true));
    const services = await ServiceRate.getData(querystring);
    if (!services.error && services.data) {
      services.data.forEach((service, i) => {
        service.key = i;
      }); // This is necessary to remove warning in console for unique key row
      setState((prevState) => ({
        ...prevState,
        tableContent: get(services, "data", []),
        pagination: {
          currentPage: services.currentPage ? services.currentPage + 1 : paginationDefaults.page,
          size: get(services, "pageSize", paginationDefaults.size),
          totalItems: get(services, "totalItems", paginationDefaults.totalItems),
        },
      }));
    } else {
      showError(get(services, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const columns = [
    {
      title: t("serviceRates.table.name"),
      dataIndex: "id",
      key: "id",
      render: (value, row) => {
        return `${value} | ${row.name}`;
      },
    },
    {
      title: t("serviceRates.table.price"),
      dataIndex: "price",
      key: "price",
      className: "table-value-right",
      render: (value) => (value !== null ? formatNumber({ value, suffix: "EUR", lang: currentLanguage }) : "-"),
    },
    {
      title: t("general.actions"),
      dataIndex: "",
      key: "x",
      width: 80,
      render: (record) => {
        return (
          <div className="space-around">
            {filters.obj.month <= state.lastMonthClosed ? (
              <EditOutlined className="icon-medium noCursorPointer disabled" />
            ) : (
              <EditOutlined className="icon-medium" onClick={() => editCallback(record.id)} />
            )}
          </div>
        );
      },
    },
  ];

  const editCallback = async (id) => {
    setState((prevState) => ({ ...prevState, loading: true, modal: { visible: true, type: "edit" } }));
    const selectedService = await ServiceRate.getDetails(id, filters.string);
    if (!selectedService.error) {
      setState((prevState) => ({ ...prevState, selectedService }));
    } else {
      showError(get(selectedService, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, loading: false }));
  };

  const closeModal = () => {
    formikServices.resetForm();
    setState((prevState) => ({
      ...prevState,
      selectedService: {},
      productCategories: [],
      modal: { visible: false, type: state.modal.type },
    }));
  };

  const monthValue = filters.obj?.month
    ? dayjs(filters.obj.month.toString(), "YYYYMM", true)
    : dayjs(currentMonth, "YYYYMM", true);

  const formikFilter = useFormik({
    initialValues: {
      month: monthValue,
      id: get(state.selectedService, "value", ""),
      name: get(state.selectedService, "name", ""),
    },
    enableReinitialize: true,
    validate: (values) => {
      let errors = {};
      if (!values.month) errors.month = "Required!";
      return errors;
    },
    onSubmit: (values) => {
      const queryString = buildQueryString(filters.obj, { month: dayjs(values.month).format("YYYYMM") });
      dispatch(setFilters(queryString));
      dataList(queryString.string);
    },
  });

  const resetFilter = () => {
    const queryString = buildQueryString({}, { month: state.lastMonthClosed });
    dispatch(setFilters(queryString));
    dataList(queryString.string);
    setState((prevState) => ({ ...prevState, projectsActivities: [] }));
  };

  const fieldsFilter = [
    {
      type: "month",
      isRequired: true,
      id: "month",
      name: "month",
      format: dateFormat,
      locale: langDate[`locale_${toLower(currentLanguage)}`],
      label: t("serviceRates.month"),
      value: formikFilter.values.month,
      onChange: (date) => {
        formikFilter.setFieldValue("month", date);
      },
    },
  ];

  return (
    <>
      <Filter formikForm={formikFilter} fields={fieldsFilter} onReset={resetFilter} />
      <Table
        pagination={{
          current: state.pagination.currentPage,
          pageSize: state.pagination.size,
          total: state.pagination.totalItems,
          onChange: (page, pageSize) => {
            const queryString = buildQueryString(filters.obj, { page: toString(page - 1), size: pageSize });
            dispatch(setFilters(queryString));
            setState((prevState) => ({
              ...prevState,
              currentPage: page,
              size: pageSize,
              totalItems: state.pagination.totalItems,
            }));
            dataList(queryString.string);
          },
        }}
        columns={columns}
        dataSource={state.tableContent}
        size="small"
      />

      <Modal
        width={700}
        title={state.modal.type === "edit" ? state.selectedService.id : t("services.new")}
        open={state.modal.visible}
        onCancel={closeModal}
        footer={[
          <Button
            key="okButton"
            isDisabled={!(formikServices.dirty && formikServices.isValid)}
            currentIcon={<SaveOutlined className="icon-medium" />}
            clickCallback={formikServices.submitForm}
          />,
        ]}
      >
        {state.loading ? (
          <div className="container-spinner-positioning">
            <Spin spinning={state.loading} className="spinner-positioning" />
          </div>
        ) : (
          ""
        )}
        <>
          <Row gutter={[40, 24]}>
            <Col lg={12} md={12} xs={24} sm={12}>
              <Row>
                <label className="fw-bold  mb-10 mt-10" htmlFor="id">
                  {t("serviceRates.table.id")}
                </label>
              </Row>
              <Row className="mb-2em">
                <Input
                  id="id"
                  name="id"
                  onChange={formikServices.handleChange}
                  onBlur={formikServices.handleBlur}
                  value={formikServices.values.id}
                  disabled
                />
              </Row>
            </Col>
            <Col lg={12} md={12} xs={24} sm={12}>
              <Row>
                <label className="fw-bold  mb-10 mt-10" htmlFor="name">
                  {t("serviceRates.table.name")}
                </label>
              </Row>
              <Row className="mb-2em">
                <Input
                  id="name"
                  name="name"
                  onChange={formikServices.handleChange}
                  onBlur={formikServices.handleBlur}
                  value={formikServices.values.name}
                  disabled
                />
              </Row>
            </Col>
          </Row>
          <Row gutter={[40, 24]}>
            <Col lg={12} md={12} xs={24} sm={12}>
              <Row>
                <label className="fw-bold  mb-10 mt-10" htmlFor="price">
                  {t("serviceRates.table.price")}*
                </label>
              </Row>
              <Row className="mb-2em">
                <Input
                  type="number"
                  required
                  id="price"
                  name="price"
                  onChange={formikServices.handleChange}
                  onBlur={formikServices.handleBlur}
                  value={formikServices.values.price}
                />
              </Row>
            </Col>
          </Row>
        </>
      </Modal>
    </>
  );
};

export default ServiceRates;
